import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, CheckCircle2, AlertTriangle, Rocket, Loader2, Play } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const csv = (arr) => (arr || []).join(', ');
const parseCsv = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

// Éditeur de procédure conditionnelle (métadonnées + étapes en JSON structuré).
const KbProcedureEditor = ({ slug, onClose }) => {
  const [proc, setProc] = useState(null);
  const [stepsText, setStepsText] = useState('[]');
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/assistant/procedures/${slug}`);
      setProc(r.data);
      setStepsText(JSON.stringify(r.data.steps || [], null, 2));
    } catch (e) { toast.error('Chargement impossible'); }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    let steps;
    try { steps = JSON.parse(stepsText); }
    catch (e) { toast.error('JSON des étapes invalide'); return; }
    setSaving(true);
    try {
      await api.put(`/admin/assistant/procedures/${slug}`, {
        title: proc.title, description: proc.description,
        problem_categories: parseCsv(proc._cat ?? csv(proc.problem_categories)),
        devices: parseCsv(proc._dev ?? csv(proc.devices)),
        apps: parseCsv(proc._app ?? csv(proc.apps)),
        operating_systems: parseCsv(proc._os ?? csv(proc.operating_systems)),
        connection_types: parseCsv(proc._conn ?? csv(proc.connection_types)),
        entry_step: proc.entry_step, priority: Number(proc.priority) || 50,
        progress_outline: proc.progress_outline || [],
        steps,
      });
      toast.success('Enregistré');
      load();
    } catch (e) { toast.error('Enregistrement échoué'); }
    finally { setSaving(false); }
  };

  const validate = async () => {
    const r = await api.get(`/admin/assistant/procedures/${slug}/validate`);
    setValidation(r.data);
    if (r.data.valid) toast.success('Procédure valide'); else toast.error('Procédure invalide');
  };

  const publish = async () => {
    try {
      await api.post(`/admin/assistant/procedures/${slug}/status`, { status: 'publie', comment: 'Publication admin' });
      toast.success('Publiée — disponible immédiatement pour Nova');
      load();
    } catch (e) {
      const v = e.response?.data?.detail?.validation;
      if (v) { setValidation(v); toast.error('Corrigez les erreurs avant publication'); }
      else toast.error('Publication échouée');
    }
  };

  const simulate = async () => {
    try {
      const r = await api.post(`/admin/assistant/procedures/${slug}/simulate`);
      toast.success(`Simulation démarrée (étape « ${r.data.step_key} »). Aucun effet de bord.`);
    } catch (e) { toast.error('Simulation impossible'); }
  };

  if (loading || !proc) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <Loader2 className="h-6 w-6 text-[#FF6B35] animate-spin" />
      </div>
    );
  }

  const F = (label, key, ph) => (
    <div>
      <label className="text-[11px] text-[#6B6B6E]">{label}</label>
      <input
        value={proc[`_${key}`] ?? (Array.isArray(proc[({ cat: 'problem_categories', dev: 'devices', app: 'apps', os: 'operating_systems', conn: 'connection_types' }[key])]) ? csv(proc[({ cat: 'problem_categories', dev: 'devices', app: 'apps', os: 'operating_systems', conn: 'connection_types' }[key])]) : (proc[key] || ''))}
        onChange={(e) => setProc({ ...proc, [`_${key}`]: e.target.value })}
        placeholder={ph}
        className="w-full mt-1 bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-2 py-1.5 text-sm text-white"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="proc-editor">
      <div className="bg-[#141416] border border-[#3A3A3D] rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#3A3A3D] sticky top-0 bg-[#141416]">
          <div>
            <h3 className="text-white font-semibold">Éditeur de procédure</h3>
            <p className="text-xs text-[#6B6B6E]">{slug} · statut <span className="text-[#00FFFF]">{proc.status}</span> · v{proc.version || 0}</p>
          </div>
          <button onClick={onClose} className="p-2 text-[#6B6B6E] hover:text-white" data-testid="proc-editor-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11px] text-[#6B6B6E]">Titre</label>
            <input value={proc.title || ''} onChange={(e) => setProc({ ...proc, title: e.target.value })}
              className="w-full mt-1 bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-2 py-1.5 text-sm text-white" data-testid="proc-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {F('Catégories (virgules)', 'cat', 'Lecture, Installation')}
            {F('Appareils (virgules)', 'dev', 'Samsung Smart TV')}
            {F('Applications (virgules)', 'app', 'IPTV Smarters Pro')}
            {F('Systèmes (virgules)', 'os', 'Tizen, webOS')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {F('Types connexion (virgules)', 'conn', 'xtream, m3u')}
            <div>
              <label className="text-[11px] text-[#6B6B6E]">Étape de départ (entry_step)</label>
              <input value={proc.entry_step || ''} onChange={(e) => setProc({ ...proc, entry_step: e.target.value })}
                className="w-full mt-1 bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-2 py-1.5 text-sm text-white" data-testid="proc-entry" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-[#6B6B6E]">Étapes (JSON) — key, phase, title, instruction, quick_replies[{'{label,value}'}], next_map{'{value:target}'}, is_final</label>
            <textarea value={stepsText} onChange={(e) => setStepsText(e.target.value)} rows={12}
              className="w-full mt-1 bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-2 py-2 text-xs font-mono text-[#E8E8E8]" data-testid="proc-steps" />
          </div>

          {validation && (
            <div className={`rounded-lg p-3 text-xs ${validation.valid ? 'bg-[#00FF88]/10 border border-[#00FF88]/30 text-[#00FF88]' : 'bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 text-[#FF3B5C]'}`} data-testid="proc-validation">
              {validation.valid ? '✓ Procédure valide' : (
                <ul className="list-disc pl-4 space-y-0.5">{validation.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 p-4 border-t border-[#3A3A3D] sticky bottom-0 bg-[#141416]">
          <button onClick={save} disabled={saving} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-white hover:border-[#FF6B35]" data-testid="proc-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
          </button>
          <button onClick={validate} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#FFB800] hover:border-[#FFB800]" data-testid="proc-validate">
            <AlertTriangle className="h-4 w-4" /> Valider
          </button>
          <button onClick={simulate} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#00FFFF] hover:border-[#00FFFF]" data-testid="proc-simulate">
            <Play className="h-4 w-4" /> Simuler
          </button>
          <button onClick={publish} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-[#00FF88]/15 border border-[#00FF88]/40 text-[#00FF88] hover:bg-[#00FF88]/25 ml-auto" data-testid="proc-publish">
            <Rocket className="h-4 w-4" /> Publier
          </button>
        </div>
      </div>
    </div>
  );
};

export default KbProcedureEditor;
